import { forEachDiagnostic } from '@codemirror/lint';
import type { Compartment } from '@codemirror/state';
import { gutter, GutterMarker } from '@codemirror/view';
import type { BlockInfo, EditorView } from '@codemirror/view';
import type { RefObject } from 'react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';

import { HealthIndicator } from '../../../content/health-indicator/HealthIndicator.js';

type GutterSeverity = 'error' | 'warning';

/**
 * Minimal shape shared by CodeMirror's `Diagnostic` and our `InlineDiagnostic`,
 * covering only the fields the gutter needs — avoids casting between the two.
 */
type LineDiagnosticInput = {
  from: number;
  severity?: 'hint' | 'info' | 'warning' | 'error';
};

const gutterRoots = new WeakMap<Node, Root>();

class LintGutterMarker extends GutterMarker {
  constructor(private readonly _severity: GutterSeverity) {
    super();
  }

  override eq(other: GutterMarker): boolean {
    return (
      other instanceof LintGutterMarker && this._severity === other._severity
    );
  }

  override toDOM(): Node {
    const element = document.createElement('div');
    // Decorative severity indicator; the diagnostic detail stays reachable via
    // the keyboard-accessible underline in the content area, so hide it from AT.
    element.setAttribute('aria-hidden', 'true');
    element.dataset.severity = this._severity; // test hook, not public API
    const root = createRoot(element);
    gutterRoots.set(element, root);
    root.render(
      <HealthIndicator
        status={this._severity === 'error' ? 'critical' : 'warning'}
        visual="shape"
      />,
    );
    return element;
  }

  override destroy(dom: Node): void {
    gutterRoots.get(dom)?.unmount();
    gutterRoots.delete(dom);
  }
}

/**
 * Returns the highest severity (`error` beats `warning`) among the diagnostics
 * that start on the line at `lineFrom`. Multi-line diagnostics are attributed to
 * the line containing `d.from`; `info`/`hint` diagnostics are ignored. Returns
 * `null` when the line has no `error`/`warning` diagnostic.
 *
 * @param diagnostics - all diagnostics in the editor
 * @param lineFrom - document offset of the line's first character
 * @param getLineFrom - maps any document position to its line's start offset
 * @internal
 */
export function _buildLineMarker(
  diagnostics: readonly LineDiagnosticInput[],
  lineFrom: number,
  getLineFrom: (pos: number) => number,
): GutterSeverity | null {
  let severity: GutterSeverity | null = null;
  for (const d of diagnostics) {
    if (d.severity !== 'error' && d.severity !== 'warning') {
      continue;
    }
    if (getLineFrom(d.from) !== lineFrom) {
      continue;
    }
    if (d.severity === 'error') {
      return 'error';
    }
    severity = 'warning';
  }
  return severity;
}

/**
 * Builds the lint gutter extension. The column is always rendered (matching the fold
 * gutter) so the editor never reflows when markers appear or disappear.
 *
 * @internal
 */
export function getLintGutterExtension(shouldShowMarkers: boolean) {
  return gutter({
    class: 'cm-gutter-lint-indicators',
    lineMarker(view: EditorView, line: BlockInfo) {
      if (!shouldShowMarkers) {
        return null;
      }
      const docLength = view.state.doc.length;
      const all: LineDiagnosticInput[] = [];
      forEachDiagnostic(view.state, (d) => {
        if (d.from >= 0 && d.from < docLength) {
          all.push(d);
        }
      });
      const severity = _buildLineMarker(
        all,
        line.from,
        (pos) => view.state.doc.lineAt(pos).from,
      );
      return severity === null ? null : new LintGutterMarker(severity);
    },
    lineMarkerChange: () => true,
  });
}

/**
 * Updates the lint gutter extension in `lintGutterCompartment` to show or hide markers.
 * The gutter column itself is always installed; only marker visibility toggles.
 *
 * Markers are driven by whatever diagnostics currently exist in CodeMirror's shared
 * lint state, regardless of which linter populated it.
 *
 * @internal
 */
export function configureLintGutter(
  showLintGutter: boolean | undefined,
  editorViewRef: RefObject<EditorView>,
  lintGutterCompartment: Compartment,
  isLintGutterMarkersShownRef: RefObject<boolean>,
): void {
  const shouldShow = showLintGutter === true;

  if (shouldShow === isLintGutterMarkersShownRef.current) {
    return;
  }

  isLintGutterMarkersShownRef.current = shouldShow;
  editorViewRef.current.dispatch({
    effects: lintGutterCompartment.reconfigure(
      getLintGutterExtension(shouldShow),
    ),
  });
}
